import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import NavBack from 'dashboard/components/NavBack';
import { Container, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { CaseAssignmentPolicy } from 'dashboard/models/case-assignment-policy';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { Environment } from 'dashboard/models/environment';
import { Team } from 'dashboard/models/team';
import { User } from 'dashboard/models/user';
import FormEditCaseAssignmentPolicy, {
  EditCaseAssignmentPolicyFormData,
} from 'dashboard/views/DashboardCaseAssignmentPolicyShowView/FormEditCaseAssignmentPolicy';
import Button from 'design-system/components/Button';
import PageHeader from 'design-system/components/PageHeader';
import { HStack } from 'design-system/components/Stack';
import { useFormSecurely } from 'lib/security/reactHookForm';

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

export interface DashboardCaseAssignmentPolicyShowViewProps {
  currentEnvironment: Environment;
  caseAssignmentPolicy: CaseAssignmentPolicy;
  submitting: boolean;
  users: User[];
  teams: Team[];
  caseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  caseQueues: CaseQueue[];
  updateCaseAssignmentPolicy: (values: EditCaseAssignmentPolicyFormData) => void;
}

const DashboardCaseAssignmentPolicyShowView = ({
  currentEnvironment,
  caseAssignmentPolicy,
  submitting,
  users,
  teams,
  caseTemplateBasicsOptions,
  caseQueues,
  updateCaseAssignmentPolicy,
}: DashboardCaseAssignmentPolicyShowViewProps) => {
  const environmentName = currentEnvironment?.attributes?.name;
  const caseAssignmentPolicyAttributes = caseAssignmentPolicy.attributes;

  const initialValues = useMemo<EditCaseAssignmentPolicyFormData>(() => {
    return {
      name: caseAssignmentPolicyAttributes.name,
      distributionType: caseAssignmentPolicyAttributes['distribution-type'],
      assignmentOrderBy: caseAssignmentPolicyAttributes['assignment-order-by'],
      maxCapacity: caseAssignmentPolicyAttributes['max-capacity'],
      allowedUserAvailabilities: caseAssignmentPolicyAttributes['allowed-user-availabilities'],
      unassignUserAvailabilities: caseAssignmentPolicyAttributes['unassign-user-availabilities'],
      userIds: caseAssignmentPolicyAttributes['assignee-user-ids'],
      teamIds: caseAssignmentPolicyAttributes['assignee-team-ids'],
      caseSourceType:
        caseAssignmentPolicyAttributes['case-source-list']['source-type'] || 'case_template',
      caseSourceIds: caseAssignmentPolicyAttributes['case-source-list']['source-ids'],
    };
  }, [caseAssignmentPolicyAttributes]);

  const caseAssignmentPolicyFormContext = useFormSecurely({
    mode: 'onChange',
    defaultValues: initialValues,
  });

  // Reset the initial values for the form when the initial values get reset (e.g. when the case template
  // is updated) so that the form state `isDirty`, etc. stays up to date.
  useEffect(() => {
    caseAssignmentPolicyFormContext.reset(initialValues);
  }, [initialValues]);

  const { isDirty, isValid } = caseAssignmentPolicyFormContext.formState;

  return (
    <>
      <Container>
        <Helmet title={`${caseAssignmentPolicyAttributes.name} - Assignment Policies - Cases`} />
        <PageHeader
          title={caseAssignmentPolicyAttributes.name}
          prepend={
            <HorizontalContainer>
              <NavBack
                to='/dashboard/case-assignment-policies'
                dataTest='case-assignment-policy-show__header--nav-back'
              />
            </HorizontalContainer>
          }
        >
          <HStack>
            <Button
              id={`case-assignment-policy-show__save-button--${environmentName}`}
              data-test={`case-assignment-policy-show__save-button--${environmentName}`}
              onClick={() =>
                caseAssignmentPolicyFormContext.handleSubmit(updateCaseAssignmentPolicy)()
              }
              isPending={submitting}
              disabled={!isDirty || !isValid}
            >
              Save
            </Button>
          </HStack>
        </PageHeader>
        <Section>
          <FormProvider {...caseAssignmentPolicyFormContext}>
            <FormEditCaseAssignmentPolicy
              users={users}
              teams={teams}
              caseTemplateBasicsOptions={caseTemplateBasicsOptions}
              caseQueues={caseQueues}
              updateCaseAssignmentPolicy={updateCaseAssignmentPolicy}
            />
          </FormProvider>
        </Section>
      </Container>
    </>
  );
};

export default DashboardCaseAssignmentPolicyShowView;
